import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Brush,
  Eraser,
  ImagePlus,
  Layers,
  Lock,
  MousePointer2,
  Package,
  Paintbrush,
  RotateCcw,
  RotateCw,
  Save,
  Send,
  Trash2,
  Unlock,
  Upload,
} from "lucide-react";
import MarketplacePreviewCard from "./MarketplacePreviewCard";

export const EMPTY_CREATOR_PRODUCT = {
  id: "",
  name: "새 서버 카드",
  category: "server",
  price: 1000,
  description: "내 서버를 특별하게 꾸며주는 서버 카드 디자인입니다.",
  tags: [],
  effect: {
    borderColor: "#5865F2",
    glowColor: "#5865F2",
    borderWidth: 2,
    glowBlur: 14,
    glowOpacity: 0.35,
    radius: 12,
  },
  artwork: {
    enabled: true,
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    radius: 0,
    mediaSrc: "",
    imageData: "",
    fit: "fill",
    blendMode: "normal",
    mediaOpacity: 1,
    drawingOpacity: 1,
    layers: [],
  },
};

function cloneProduct(product) {
  return JSON.parse(JSON.stringify(product || EMPTY_CREATOR_PRODUCT));
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function uid() {
  return `layer_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function normalizeAngle(value) {
  return ((value + 180) % 360 + 360) % 360 - 180;
}

const DrawingCanvas = forwardRef(function DrawingCanvas(
  { source, sourceUsesBleed, enabled, color, size, eraser, onDirty },
  ref
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const undoRef = useRef([]);

  const context = () => canvasRef.current?.getContext("2d", { willReadFrequently: true });

  const pushUndo = () => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (!canvas || !ctx) return;
    try {
      undoRef.current = [...undoRef.current.slice(-11), ctx.getImageData(0, 0, canvas.width, canvas.height)];
    } catch (_error) {
      undoRef.current = [];
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    undoRef.current = [];
    if (!source) return;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (sourceUsesBleed) {
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        return;
      }
      const bleedX = canvas.width / 12;
      const bleedY = canvas.height / 12;
      ctx.drawImage(image, bleedX, bleedY, canvas.width - bleedX * 2, canvas.height - bleedY * 2);
    };
    image.src = source;
  }, [source, sourceUsesBleed]);

  useImperativeHandle(ref, () => ({
    async getBlob() {
      const canvas = canvasRef.current;
      return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    },
    clear() {
      const canvas = canvasRef.current;
      const ctx = context();
      if (!canvas || !ctx) return;
      pushUndo();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onDirty();
    },
    undo() {
      const canvas = canvasRef.current;
      const ctx = context();
      const snapshot = undoRef.current.pop();
      if (!canvas || !ctx || !snapshot) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.putImageData(snapshot, 0, 0);
      onDirty();
    },
  }));

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvasRef.current.width,
      y: ((event.clientY - rect.top) / rect.height) * canvasRef.current.height,
    };
  };

  const begin = (event) => {
    if (!enabled) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pushUndo();
    drawingRef.current = true;
    const ctx = context();
    const next = point(event);
    ctx.beginPath();
    ctx.moveTo(next.x, next.y);
  };

  const move = (event) => {
    if (!drawingRef.current || !enabled) return;
    event.preventDefault();
    const ctx = context();
    const next = point(event);
    ctx.globalCompositeOperation = eraser ? "destination-out" : "source-over";
    ctx.strokeStyle = color;
    ctx.lineWidth = Number(size) * 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onDirty();
  };

  return (
    <canvas
      ref={canvasRef}
      width={1008}
      height={1260}
      onPointerDown={begin}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      className={`absolute -left-[10%] -top-[10%] z-[70] h-[120%] w-[120%] ${enabled ? "cursor-crosshair pointer-events-auto" : "pointer-events-none"}`}
      style={{ touchAction: "none" }}
    />
  );
});

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold text-gray-300">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-[11px] leading-4 text-gray-500">{hint}</span>}
    </label>
  );
}

function RangeField({ label, value, min, max, step = 1, onChange, suffix = "" }) {
  return (
    <Field label={`${label} · ${value}${suffix}`}>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} className="w-full accent-[#5865F2]" />
    </Field>
  );
}

function ToolSection({ title, icon, children }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.22, ease: "easeOut" }}
      className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-lg backdrop-blur-sm transition-colors hover:border-white/15"
    >
      <div className="flex items-center gap-2 border-b border-white/10 bg-black/10 px-4 py-3.5 text-sm font-black text-white">
        {icon}
        {title}
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </motion.section>
  );
}

function SegmentedOptions({ value, options, onChange, columns = 2 }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`min-h-10 rounded-lg border px-2 py-2 text-xs font-bold transition-all ${
            value === option.value
              ? "border-indigo-500 bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
              : "border-white/10 bg-white/5 text-gray-300 hover:bg-white/10"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default function ServerCardDesigner({ initialProduct, onSave, onSubmit, onUploadAsset, busy }) {
  const [product, setProduct] = useState(() => cloneProduct(initialProduct));
  const [selectedLayerId, setSelectedLayerId] = useState("");
  const [drawMode, setDrawMode] = useState(false);
  const [drawColor, setDrawColor] = useState("#ffffff");
  const [drawSize, setDrawSize] = useState(8);
  const [eraser, setEraser] = useState(false);
  const [drawingDirty, setDrawingDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saveState, setSaveState] = useState("");
  const editorRef = useRef(null);
  const canvasRef = useRef(null);
  const interactionRef = useRef(null);
  const rafRef = useRef(null);

  const LAYER_BLEED = 10;
  const MIN_LAYER_SIZE = 5;
  const MAX_LAYER_SIZE = 120;
  const MIN_VISIBLE = 5;

  const layerPositionBounds = (size) => ({
    min: Math.max(-LAYER_BLEED, MIN_VISIBLE - size),
    max: Math.min(100 - MIN_VISIBLE, 100 + LAYER_BLEED - size),
  });

  const constrainLayer = (layer) => {
    const width = clamp(Number(layer.width) || MIN_LAYER_SIZE, MIN_LAYER_SIZE, MAX_LAYER_SIZE);
    const height = clamp(Number(layer.height) || MIN_LAYER_SIZE, MIN_LAYER_SIZE, MAX_LAYER_SIZE);
    const xBounds = layerPositionBounds(width);
    const yBounds = layerPositionBounds(height);
    return {
      ...layer,
      width,
      height,
      x: clamp(Number(layer.x) || 0, xBounds.min, xBounds.max),
      y: clamp(Number(layer.y) || 0, yBounds.min, yBounds.max),
    };
  };

  useEffect(() => {
    setProduct(cloneProduct(initialProduct));
    setSelectedLayerId("");
    setDrawingDirty(false);
    setSaveState("");
  }, [initialProduct]);

  const setEffect = (field, value) => setProduct((current) => ({
    ...current,
    effect: { ...current.effect, [field]: value },
  }));

  const updateLayer = (id, patch) => setProduct((current) => ({
    ...current,
    artwork: {
      ...current.artwork,
      enabled: true,
      layers: (current.artwork.layers || []).map((layer) => layer.id === id ? constrainLayer({ ...layer, ...patch }) : layer),
    },
  }));

  const selectedLayer = (product.artwork.layers || []).find((layer) => layer.id === selectedLayerId);

  useEffect(() => {
    const onMove = (event) => {
      const active = interactionRef.current;
      if (!active || event.pointerId !== active.pointerId) return;
      event.preventDefault();
      const dx = ((event.clientX - active.clientX) / active.rect.width) * 100;
      const dy = ((event.clientY - active.clientY) / active.rect.height) * 100;
      let patch;
      if (active.mode === "move") {
        const xBounds = layerPositionBounds(active.layer.width);
        const yBounds = layerPositionBounds(active.layer.height);
        patch = {
          x: Math.round(clamp(active.layer.x + dx, xBounds.min, xBounds.max) * 10) / 10,
          y: Math.round(clamp(active.layer.y + dy, yBounds.min, yBounds.max) * 10) / 10,
        };
      } else if (active.mode === "resize") {
        let width = clamp(active.layer.width + dx, MIN_LAYER_SIZE, Math.min(MAX_LAYER_SIZE, 100 + LAYER_BLEED - active.layer.x));
        let height = clamp(active.layer.height + dy, MIN_LAYER_SIZE, Math.min(MAX_LAYER_SIZE, 100 + LAYER_BLEED - active.layer.y));
        if (active.layer.preserveAspectRatio) {
          height = clamp(width * (active.layer.height / active.layer.width), MIN_LAYER_SIZE, Math.min(MAX_LAYER_SIZE, 100 + LAYER_BLEED - active.layer.y));
          width = height * (active.layer.width / active.layer.height);
        }
        patch = { width: Math.round(width * 10) / 10, height: Math.round(height * 10) / 10 };
      } else {
        const angle = (Math.atan2(event.clientY - active.centerY, event.clientX - active.centerX) * 180) / Math.PI;
        let rotation = normalizeAngle(active.layer.rotation + angle - active.startAngle);
        if (event.shiftKey) rotation = Math.round(rotation / 15) * 15;
        patch = { rotation: Math.round(rotation * 10) / 10 };
      }
      active.pending = patch;
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          const latest = interactionRef.current;
          if (latest?.pending) updateLayer(latest.id, latest.pending);
          rafRef.current = null;
        });
      }
    };
    const onEnd = (event) => {
      if (interactionRef.current?.pointerId === event.pointerId) interactionRef.current = null;
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  });

  const beginInteraction = (event, layer, mode) => {
    if (drawMode) return;
    event.preventDefault();
    event.stopPropagation();
    setSelectedLayerId(layer.id);
    const rect = editorRef.current.getBoundingClientRect();
    const centerX = rect.left + ((layer.x + layer.width / 2) / 100) * rect.width;
    const centerY = rect.top + ((layer.y + layer.height / 2) / 100) * rect.height;
    interactionRef.current = {
      id: layer.id,
      mode,
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      rect,
      centerX,
      centerY,
      startAngle: (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) / Math.PI,
      layer: { rotation: 0, ...layer },
      pending: null,
    };
  };

  const uploadLayer = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const asset = await onUploadAsset(file);
      const width = 42;
      const aspectHeight = asset.width && asset.height ? width * (asset.height / asset.width) * 0.8 : 28;
      const layer = {
        id: uid(),
        src: asset.url,
        x: 8,
        y: 8,
        width,
        height: clamp(Math.round(aspectHeight * 10) / 10, 8, 70),
        rotation: 0,
        radius: 0,
        fit: "contain",
        preserveAspectRatio: true,
        blendMode: "normal",
        opacity: 1,
      };
      setProduct((current) => ({
        ...current,
        artwork: { ...current.artwork, enabled: true, layers: [...(current.artwork.layers || []), layer] },
      }));
      setSelectedLayerId(layer.id);
    } finally {
      setUploading(false);
    }
  };

  const removeLayer = (id) => {
    setProduct((current) => ({
      ...current,
      artwork: { ...current.artwork, layers: (current.artwork.layers || []).filter((layer) => layer.id !== id) },
    }));
    setSelectedLayerId("");
  };

  const prepareProduct = async () => {
    let next = cloneProduct(product);
    if (drawingDirty) {
      const blob = await canvasRef.current.getBlob();
      if (blob) {
        const asset = await onUploadAsset(new File([blob], "card-drawing.png", { type: "image/png" }));
        next.artwork.imageData = asset.url;
        next.artwork.enabled = true;
        next.artwork.drawingBleed = true;
        setProduct(next);
        setDrawingDirty(false);
      }
    }
    return next;
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const next = await prepareProduct();
      const saved = await onSave(next);
      if (saved) {
        setProduct(cloneProduct(saved));
        setSaveState("saved");
      } else {
        setSaveState("error");
      }
    } catch (_error) {
      setSaveState("error");
    }
  };

  const submit = async () => {
    const next = await prepareProduct();
    const saved = await onSave(next);
    if (saved?.id) {
      setProduct(cloneProduct(saved));
      await onSubmit(saved.id);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-150px)] grid-cols-1 bg-[#111] xl:grid-cols-[330px_minmax(420px,1fr)_320px]">
      <aside className="border-b border-white/10 bg-[#111] p-4 xl:border-b-0 xl:border-r">
        <div className="space-y-4">
          <ToolSection title="상품 정보" icon={<Package className="h-4 w-4 text-indigo-400" />}>
            <p className="text-xs leading-5 text-gray-500">저장 후 심사를 요청하면 승인된 버전이 상점에 공개됩니다.</p>
            <Field label="상품명">
              <input value={product.name} maxLength={80} onChange={(event) => setProduct({ ...product, name: event.target.value })} className="creator-input" />
            </Field>
            <Field label="가격" hint="1,000원부터 100,000원까지 설정할 수 있어요.">
              <div className="relative"><input type="number" min="1000" max="100000" step="100" value={product.price} onChange={(event) => setProduct({ ...product, price: Number(event.target.value) })} className="creator-input pr-10" /><span className="absolute right-3 top-3 text-sm text-gray-500">원</span></div>
            </Field>
            <Field label="상품 설명">
              <textarea value={product.description} maxLength={800} rows={5} onChange={(event) => setProduct({ ...product, description: event.target.value })} className="creator-input resize-none" />
            </Field>
            <Field label="태그" hint="쉼표로 구분해 최대 5개까지 입력할 수 있어요.">
              <input value={(product.tags || []).join(", ")} onChange={(event) => setProduct({ ...product, tags: event.target.value.split(",").map((tag) => tag.trim()).filter(Boolean).slice(0, 5) })} className="creator-input" placeholder="귀여운, 게임, 네온" />
            </Field>
          </ToolSection>

          <ToolSection title="테두리와 빛" icon={<Paintbrush className="h-4 w-4 text-indigo-400" />}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="테두리 색"><input type="color" value={product.effect.borderColor} onChange={(event) => setEffect("borderColor", event.target.value)} className="h-10 w-full cursor-pointer rounded-lg border border-white/10 bg-white/5 p-1" /></Field>
              <Field label="빛 색"><input type="color" value={product.effect.glowColor} onChange={(event) => setEffect("glowColor", event.target.value)} className="h-10 w-full cursor-pointer rounded-lg border border-white/10 bg-white/5 p-1" /></Field>
            </div>
            <div className="space-y-4">
              <RangeField label="테두리" value={product.effect.borderWidth} min={0} max={8} onChange={(value) => setEffect("borderWidth", value)} suffix="px" />
              <RangeField label="빛 번짐" value={product.effect.glowBlur} min={0} max={40} onChange={(value) => setEffect("glowBlur", value)} suffix="px" />
              <RangeField label="빛 투명도" value={product.effect.glowOpacity} min={0} max={1} step={0.05} onChange={(value) => setEffect("glowOpacity", value)} />
              <RangeField label="모서리" value={product.effect.radius} min={0} max={32} onChange={(value) => setEffect("radius", value)} suffix="px" />
            </div>
          </ToolSection>
        </div>
      </aside>

      <section className="flex min-h-[680px] flex-col items-center bg-[#0d0e11] px-4 py-7 sm:px-8">
        <div className="mb-5 flex w-full max-w-[460px] flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-xs font-bold text-gray-500">실제 카드 비율 미리보기</span>
            <p className="text-sm font-bold text-white">레이어를 마우스나 손가락으로 움직여보세요.</p>
          </div>
          <button type="button" onClick={() => setDrawMode((value) => !value)} className={`flex h-10 items-center gap-2 rounded-lg border px-4 text-sm font-bold transition-all ${drawMode ? "border-indigo-500 bg-indigo-600 text-white shadow-lg shadow-indigo-600/20" : "border-white/10 bg-white/5 text-gray-200 hover:bg-white/10"}`}>
            {drawMode ? <MousePointer2 className="h-4 w-4" /> : <Brush className="h-4 w-4" />}
            {drawMode ? "레이어 편집" : "직접 그리기"}
          </button>
        </div>

        {drawMode && (
          <div className="mb-4 flex w-full max-w-[460px] flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-2 shadow-lg backdrop-blur-sm">
            <button type="button" onClick={() => setEraser(false)} title="브러시" className={`creator-tool-button ${!eraser ? "is-active" : ""}`}><Paintbrush className="h-4 w-4" /></button>
            <button type="button" onClick={() => setEraser(true)} title="지우개" className={`creator-tool-button ${eraser ? "is-active" : ""}`}><Eraser className="h-4 w-4" /></button>
            <input type="color" value={drawColor} onChange={(event) => setDrawColor(event.target.value)} className="h-9 w-10 rounded-md bg-transparent" aria-label="브러시 색" />
            <input type="range" min="2" max="30" value={drawSize} onChange={(event) => setDrawSize(Number(event.target.value))} className="min-w-[90px] flex-1 accent-[#5865F2]" aria-label="브러시 크기" />
            <button type="button" onClick={() => canvasRef.current.undo()} title="되돌리기" className="creator-tool-button"><RotateCcw className="h-4 w-4" /></button>
            <button type="button" onClick={() => canvasRef.current.clear()} title="그림 지우기" className="creator-tool-button"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}

        <div className="flex w-full max-w-[552px] justify-center py-[11%]">
          <div ref={editorRef} className="relative w-5/6 max-w-[460px] select-none" style={{ aspectRatio: "4 / 5", touchAction: "none" }} onPointerDown={() => !drawMode && setSelectedLayerId("")}>
          <div className="pointer-events-none absolute -inset-[10%] z-10 border border-dashed border-white/10" />
          <MarketplacePreviewCard product={product} className="h-full" />
          {(product.artwork.layers || []).map((layer) => (
            <div
              key={layer.id}
              onPointerDown={(event) => beginInteraction(event, layer, "move")}
              className={`absolute z-[60] cursor-move border ${selectedLayerId === layer.id ? "border-[#aab0ff] bg-[#5865F2]/5" : "border-transparent hover:border-white/40"}`}
              style={{
                left: `${layer.x}%`,
                top: `${layer.y}%`,
                width: `${layer.width}%`,
                height: `${layer.height}%`,
                transform: `rotate(${Number(layer.rotation) || 0}deg)`,
                transformOrigin: "center center",
                touchAction: "none",
              }}
            >
              {selectedLayerId === layer.id && (
                <>
                  <span className="pointer-events-none absolute bottom-full left-1/2 h-7 w-px bg-[#aab0ff]" />
                  <button
                    type="button"
                    aria-label="레이어 회전"
                    title="드래그해서 회전"
                    onPointerDown={(event) => beginInteraction(event, layer, "rotate")}
                    className="absolute -top-10 left-1/2 flex h-7 w-7 cursor-grab items-center justify-center rounded-full border-2 border-white bg-indigo-600 text-white shadow-lg active:cursor-grabbing"
                    style={{ transform: `translateX(-50%) rotate(${-(Number(layer.rotation) || 0)}deg)` }}
                  >
                    <RotateCw className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" aria-label="레이어 크기 조절" onPointerDown={(event) => beginInteraction(event, layer, "resize")} className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-sm border-2 border-white bg-[#5865F2] shadow" />
                </>
              )}
            </div>
          ))}
          <DrawingCanvas
            ref={canvasRef}
            source={product.artwork.imageData}
            sourceUsesBleed={product.artwork.drawingBleed}
            enabled={drawMode}
            color={drawColor}
            size={drawSize}
            eraser={eraser}
            onDirty={() => {
              setDrawingDirty(true);
              setProduct((current) => ({
                ...current,
                artwork: { ...current.artwork, drawingBleed: true },
              }));
            }}
          />
          </div>
        </div>

        <div className="mt-6 flex w-full max-w-[460px] flex-col gap-2 sm:flex-row">
          <button type="button" disabled={busy || uploading} onClick={save} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 font-bold text-white hover:bg-white/10 disabled:opacity-50"><Save className="h-4 w-4" /> 초안 저장</button>
          <button type="button" disabled={busy || uploading} onClick={submit} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-indigo-600 font-bold text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-500 disabled:opacity-50"><Send className="h-4 w-4" /> 저장 후 심사 요청</button>
        </div>
        <p className={`mt-2 min-h-5 text-xs font-bold ${saveState === "error" ? "text-red-400" : saveState === "saved" ? "text-emerald-400" : "text-gray-500"}`} aria-live="polite">
          {saveState === "saving" && "초안을 저장하고 있습니다..."}
          {saveState === "saved" && "초안이 저장되었습니다. 이후 저장은 같은 초안에 반영됩니다."}
          {saveState === "error" && "초안을 저장하지 못했습니다. 위 오류 메시지를 확인해주세요."}
        </p>
      </section>

      <aside className="space-y-4 border-t border-white/10 bg-[#111] p-4 xl:border-l xl:border-t-0">
        <ToolSection title="레이어" icon={<Layers className="h-4 w-4 text-indigo-400" />}>
        <div className="flex items-center justify-end">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-[#5865F2] px-3 py-2 text-xs font-bold text-white hover:bg-[#4f5bd5]">
            <Upload className="h-4 w-4" /> {uploading ? "업로드 중" : "이미지·GIF"}
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadLayer} disabled={uploading} className="hidden" />
          </label>
        </div>
        <p className="text-[11px] leading-4 text-gray-500">자유 비율(원본 비율 유지) · PNG, JPG, GIF, WebP · 파일당 최대 8MB</p>

        <div className="space-y-2">
          {(product.artwork.layers || []).length ? (product.artwork.layers || []).map((layer, index) => (
            <button key={layer.id} type="button" onClick={() => { setSelectedLayerId(layer.id); setDrawMode(false); }} className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-all ${selectedLayerId === layer.id ? "border-indigo-500 bg-indigo-600/15 shadow-lg shadow-indigo-600/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-black/30"><img src={layer.src} alt="" className="h-full w-full object-contain" /></div>
              <div className="min-w-0 flex-1"><p className="truncate text-xs font-bold text-white">레이어 {index + 1}</p><p className="mt-1 text-[10px] text-gray-500">{Math.round(layer.width)}% × {Math.round(layer.height)}% · {Math.round(Number(layer.rotation) || 0)}°</p></div>
            </button>
          )) : (
            <div className="rounded-lg border border-dashed border-[#3a3c44] py-10 text-center"><ImagePlus className="mx-auto h-6 w-6 text-gray-600" /><p className="mt-2 text-xs text-gray-500">이미지나 GIF를 추가해보세요.</p></div>
          )}
        </div>
        </ToolSection>

        {selectedLayer && (
          <ToolSection title="선택한 레이어" icon={<MousePointer2 className="h-4 w-4 text-indigo-400" />}>
            <div className="flex items-center justify-end"><button type="button" onClick={() => removeLayer(selectedLayer.id)} className="creator-tool-button text-red-300" title="레이어 삭제"><Trash2 className="h-4 w-4" /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="X 위치"><input type="number" value={selectedLayer.x} min="-10" max="95" onChange={(event) => updateLayer(selectedLayer.id, { x: Number(event.target.value) })} className="creator-input" /></Field>
              <Field label="Y 위치"><input type="number" value={selectedLayer.y} min="-10" max="95" onChange={(event) => updateLayer(selectedLayer.id, { y: Number(event.target.value) })} className="creator-input" /></Field>
              <Field label="너비"><input type="number" value={selectedLayer.width} min="5" max="120" onChange={(event) => updateLayer(selectedLayer.id, { width: Number(event.target.value) })} className="creator-input" /></Field>
              <Field label="높이"><input type="number" value={selectedLayer.height} min="5" max="120" onChange={(event) => updateLayer(selectedLayer.id, { height: Number(event.target.value) })} className="creator-input" /></Field>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/10 p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-xs font-bold text-gray-300">회전</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="-180"
                    max="180"
                    step="1"
                    value={Number(selectedLayer.rotation) || 0}
                    onChange={(event) => updateLayer(selectedLayer.id, { rotation: clamp(Number(event.target.value), -180, 180) })}
                    className="h-8 w-20 rounded-lg border border-white/10 bg-white/5 px-2 text-right text-xs text-white outline-none focus:border-indigo-500"
                  />
                  <span className="text-xs text-gray-500">°</span>
                  <button type="button" onClick={() => updateLayer(selectedLayer.id, { rotation: 0 })} className="creator-tool-button !h-8 !w-8" title="회전 초기화"><RotateCcw className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <input type="range" min="-180" max="180" step="1" value={Number(selectedLayer.rotation) || 0} onChange={(event) => updateLayer(selectedLayer.id, { rotation: Number(event.target.value) })} className="w-full accent-indigo-500" aria-label="레이어 회전 각도" />
            </div>
            <button type="button" onClick={() => updateLayer(selectedLayer.id, { preserveAspectRatio: !selectedLayer.preserveAspectRatio })} className={`flex w-full items-center justify-center gap-2 rounded-lg border py-2.5 text-xs font-bold transition-all ${selectedLayer.preserveAspectRatio ? "border-indigo-500 bg-indigo-600 text-white shadow-lg shadow-indigo-600/20" : "border-white/10 bg-white/5 text-gray-300 hover:bg-white/10"}`}>
              {selectedLayer.preserveAspectRatio ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
              비율 {selectedLayer.preserveAspectRatio ? "고정" : "자유 조절"}
            </button>
            <RangeField label="투명도" value={selectedLayer.opacity} min={0.1} max={1} step={0.05} onChange={(value) => updateLayer(selectedLayer.id, { opacity: value })} />
            <Field label="혼합 모드">
              <SegmentedOptions
                value={selectedLayer.blendMode}
                columns={2}
                onChange={(value) => updateLayer(selectedLayer.id, { blendMode: value })}
                options={[
                  { value: "normal", label: "기본" },
                  { value: "screen", label: "스크린" },
                  { value: "lighten", label: "밝게" },
                  { value: "multiply", label: "곱하기" },
                  { value: "overlay", label: "오버레이" },
                ]}
              />
            </Field>
          </ToolSection>
        )}
      </aside>
    </div>
  );
}
